import { type ElementType, type FC, lazy, Suspense } from 'react';

import { defaultCommunityTranslate } from '../utils/defaultCommunityTranslate';

import type { SmartDateTimeFieldRender } from '../smartDateTimeField/SmartDateTimeField';
import type { SchedulePickerContentProps } from './_components/schedulePicker/SchedulePickerContent';
import {
  type CommunitySchedulePickerTranslate,
  SchedulePickerFrame,
} from './_components/schedulePicker/SchedulePickerFrame';

export interface CommunitySchedulePickerProps {
  value?: Date;
  nextUsualTime?: Date;
  onChange: (date: Date) => void;
  /** Confirm, the default, only picks the time. Schedule sends the post at it. */
  action?: 'confirm' | 'schedule';
  /** Set while the post is sent, so Schedule takes no second click. */
  isSubmitting?: boolean;
  /** Keeps Schedule disabled while the post can't be sent, as the composer's own button is. */
  isSubmitDisabled?: boolean;
  onCancel: () => void;
  timeZone: string;
  locale?: string;
  translate?: CommunitySchedulePickerTranslate;
  /** Renders the visible title, an h2 by default. A host's dialog title component names its dialog after it. */
  titleAs?: ElementType;
  /** The host's inputs, buttons, calendar and popover for the date and time field. */
  fieldRender: SmartDateTimeFieldRender;
  autoFocus?: boolean;
  /** The team's queued posts, counted on the link to the queue. */
  queuedCount?: number;
  /** Shows a link to the team's queue in the footer. */
  onViewQueue?: () => void;
}

const SchedulePickerLoadError: FC<SchedulePickerContentProps> = ({
  translate,
  titleAs,
  onCancel,
  action,
  autoFocus,
  queuedCount,
  onViewQueue,
}) => (
  <SchedulePickerFrame
    translate={translate}
    titleAs={titleAs}
    action={action}
    onCancel={onCancel}
    focusTitle={!autoFocus}
    queuedCount={queuedCount}
    onViewQueue={onViewQueue}
  >
    <p role="alert" className="text-[13px] leading-4 text-red-500">
      {translate('community.composer.schedule.load_error')}
    </p>
  </SchedulePickerFrame>
);

// The field and chrono load on the first open. A failed load shows an error instead of throwing, since
// a host has no error boundary around the picker and a throw would unmount the composer it sits on.
const SchedulePickerContent = lazy(() =>
  import('./_components/schedulePicker/SchedulePickerContent')
    .then((module): { default: FC<SchedulePickerContentProps> } => ({ default: module.SchedulePickerContent }))
    .catch(() => ({ default: SchedulePickerLoadError })),
);

export const CommunitySchedulePicker: FC<CommunitySchedulePickerProps> = ({
  translate = defaultCommunityTranslate,
  ...props
}) => (
  <Suspense
    fallback={
      <SchedulePickerFrame
        translate={translate}
        titleAs={props.titleAs}
        onCancel={props.onCancel}
        action={props.action}
        focusTitle={!props.autoFocus}
        queuedCount={props.queuedCount}
        onViewQueue={props.onViewQueue}
      />
    }
  >
    <SchedulePickerContent translate={translate} {...props} />
  </Suspense>
);
